import React, { useEffect, useRef } from "react";
import "./RelatorioVisitaTecnicaPrint.css";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function formatarDataBR(dataISO) {
  if (!dataISO) return "";
  const [ano, mes, dia] = dataISO.split("-");
  if (!ano || !mes || !dia) return dataISO;
  return `${dia}/${mes}/${ano}`;
}

function imprimirRelatorio() {
  const styleId = "relatorio-visita-tecnica-print-orientacao";
  document.getElementById(styleId)?.remove();

  const style = document.createElement("style");
  style.id = styleId;
  style.media = "print";
  style.textContent = `
    @page {
      size: A4 portrait;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
  window.print();
}

function textoOuPadrao(valor) {
  return String(valor || "").trim();
}

function dividirEmPaginas(lista, quantidade) {
  const paginas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    paginas.push(lista.slice(i, i + quantidade));
  }
  return paginas;
}

function dividirEmLinhas(lista, quantidade) {
  const linhas = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    linhas.push(lista.slice(i, i + quantidade));
  }
  return linhas;
}

function completarPaginaFotografica(fotos = []) {
  return Array.from({ length: 6 }, (_, index) => fotos[index] || null);
}

function limparLegendaFoto(legenda) {
  return String(legenda || "")
    .trim()
    .replace(/^foto\s*\d+\s*[-–:]?\s*/i, "")
    .trim();
}

function legendaFotoRelatorio(foto, numero) {
  const numeroTexto = `FOTO ${String(numero).padStart(2, "0")}`;
  const legendaLimpa = limparLegendaFoto(foto?.legenda);
  return legendaLimpa ? `${numeroTexto} - ${legendaLimpa}` : numeroTexto;
}

function TextoEditavel({ texto }) {
  return String(texto || "")
    .split("\n")
    .map((linha, index) => (
      <React.Fragment key={index}>
        {linha}
        {index < String(texto || "").split("\n").length - 1 ? <br /> : null}
      </React.Fragment>
    ));
}

function agruparItensPorLocal(itens = []) {
  return itens.reduce((grupos, item) => {
    const local = String(item?.local || "-").trim() || "-";
    const ultimoGrupo = grupos[grupos.length - 1];

    if (ultimoGrupo?.local === local) {
      ultimoGrupo.itens.push(item);
      return grupos;
    }

    return [...grupos, { local, itens: [item] }];
  }, []);
}

function montarFotosRelatorioOrdenadas(relatorio) {
  const fotosParecer = (relatorio?.parecerItens || []).flatMap((item, itemIndex) =>
    (Array.isArray(item.fotosParecer) ? item.fotosParecer : [])
      .filter((foto) => foto?.url)
      .map((foto, fotoIndex) => ({
        ...foto,
        id: foto.id || `parecer-${itemIndex + 1}-${fotoIndex + 1}`,
        legenda: foto.legenda || item.local || "",
      }))
  );
  const fotosGerais = (relatorio?.relatorioFotografico || []).filter((foto) => foto?.url);
  return [...fotosParecer, ...fotosGerais];
}

function tituloSubsecao(numero, texto, fallback) {
  const titulo = textoOuPadrao(texto || fallback).replace(/^\d+(?:\.\d+)*\.\s*/, "");
  return `${numero}. ${titulo}`;
}

function tituloSecao(numero, texto) {
  return `${numero}. ${texto}`;
}

function CabecalhoRelatorio({ relatorio }) {
  return (
    <>
      <table className="cabecalho-visita">
        <tbody>
          <tr>
            <td rowSpan="3" className="logo-cell">
              <LogoRelatorio alt="Logo" className="logo"  />
            </td>
            <td rowSpan="3" className="titulo-principal">
              SISTEMA DE GESTÃO DA QUALIDADE
              <br />
              Relatório de visita técnica
            </td>
            <td className="revisao-cell">RQ 2.2.20</td>
          </tr>
          <tr>
            <td className="revisao-cell">Revisao 00</td>
          </tr>
          <tr>
            <td className="revisao-cell">01/06/2026</td>
          </tr>
        </tbody>
      </table>

      <div className="linha-info">
        <span>
          <strong>DATA</strong>&nbsp;&nbsp; {formatarDataBR(relatorio.dataRelatorio)}
        </span>
        <span>
          <strong>CÓDIGO:</strong> {relatorio.codigo || "-"}
        </span>
      </div>

      <div className="dados-obra">
        <div>
          <strong>EMPRESA/CONTRATANTE:</strong> {relatorio.cliente || "-"}
        </div>
        <div>
          <strong>EMPREENDIMENTO:</strong> {relatorio.obra || "-"}
        </div>
        <div>
          <strong>LOCAL:</strong> {relatorio.local || "-"}
        </div>
      </div>
    </>
  );
}

function RodapeRestrito() {
  return (
    <div className="rodape-restrito">
      DOCUMENTO PARA USO RESTRITO. REPRODUÇÃO/CÓPIA PROIBIDA SEM AUTORIZAÇÃO DA EMPRESA
    </div>
  );
}

function RelatorioFotografico({ relatorio, fotos = [] }) {
  if (!fotos.length) return null;

  return dividirEmPaginas(fotos, 6).map((paginaFotos, paginaIndex) => (
    <div className="pagina-fotografica" key={`pagina-fotos-${paginaIndex}`}>
      <CabecalhoRelatorio relatorio={relatorio} />
      <div className="faixa-relatorio-fotografico">RELATÓRIO FOTOGRÁFICO</div>

      <div className="grade-fotografica">
        {dividirEmLinhas(completarPaginaFotografica(paginaFotos), 2).map((linhaFotos, linhaIndex) => (
          <div className="linha-fotografica-bloco" key={`linha-${paginaIndex}-${linhaIndex}`}>
            <div className="linha-fotografica">
              {linhaFotos.map((foto, index) => {
                const fotoNumero = paginaIndex * 6 + linhaIndex * 2 + index + 1;
                if (!foto) {
                  return (
                    <div className="foto-relatorio-item foto-relatorio-vazia" key={`foto-vazia-${fotoNumero}`} aria-hidden="true">
                      <div className="foto-relatorio-imagem" />
                      <div className="foto-relatorio-legenda">&nbsp;</div>
                    </div>
                  );
                }

                return (
                  <div className="foto-relatorio-item" key={foto.id || foto.url || fotoNumero}>
                    <div className="foto-relatorio-imagem">
                      <img src={foto.url} alt={legendaFotoRelatorio(foto, fotoNumero)} />
                    </div>
                    <div className="foto-relatorio-legenda">
                      {legendaFotoRelatorio(foto, fotoNumero)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="linha-azul-fotos" aria-hidden="true" />
          </div>
        ))}
      </div>
      <RodapeRestrito />
    </div>
  ));
}

export default function RelatorioVisitaTecnicaPrint({
  relatorio,
  fotosRelatorio = [],
  onVoltar,
  autoPrint = false,
}) {
  const jaImprimiuRef = useRef(false);
  useNomeArquivoPdf(relatorio?.codigo);

  useEffect(() => {
    if (!autoPrint || jaImprimiuRef.current) return;
    jaImprimiuRef.current = true;
    const frame = window.requestAnimationFrame(imprimirRelatorio);
    return () => window.cancelAnimationFrame(frame);
  }, [autoPrint]);

  if (!relatorio) return null;

  const parecerItens = Array.isArray(relatorio.parecerItens) ? relatorio.parecerItens : [];
  const parecerGrupos = agruparItensPorLocal(parecerItens);
  const fotosAnexo = fotosRelatorio.length ? fotosRelatorio : montarFotosRelatorioOrdenadas(relatorio);
  const notas = Array.isArray(relatorio.notasParecer) ? relatorio.notasParecer : [];
  const mostrarBoasPraticas = textoOuPadrao(relatorio.boasPraticas);
  const numeroParecer = "3";
  const numeroIdentificacaoParecer = `${numeroParecer}.1`;
  const numeroBoasPraticas = `${numeroParecer}.2`;
  const numeroResultadoAceitacao = mostrarBoasPraticas ? `${numeroParecer}.3` : `${numeroParecer}.2`;
  const numeroAnexos = "4";
  const numeroConsideracoesFinais = "5";

  return (
    <div className="relatorio-visita-print">
      {onVoltar && (
        <div className="acoes-print">
          <button className="btn-voltar" onClick={onVoltar}>Voltar</button>
          <button className="btn-imprimir" onClick={imprimirRelatorio}>Imprimir</button>
        </div>
      )}

      <div className="pagina">
        <table className="estrutura-relatorio">
          <thead>
            <tr className="cabecalho-margem-topo">
              <td aria-hidden="true"></td>
            </tr>
            <tr>
              <td>
                <CabecalhoRelatorio relatorio={relatorio} />
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="faixa-azul">RELATÓRIO DE ENSAIO</div>

                <div className="secao">{tituloSecao("1", "OBJETIVO.")}</div>
                <div className="texto"><TextoEditavel texto={relatorio.objetivo} /></div>

                <div className="secao">{tituloSecao("2", "METODOLOGIA.")}</div>
                <div className="texto"><TextoEditavel texto={relatorio.metodologia} /></div>

                <div className="secao">{tituloSecao(numeroParecer, "PARECER TÉCNICO.")}</div>
                <div className="subsecao">{tituloSubsecao(numeroIdentificacaoParecer, relatorio.parecerTitulo, "Identificação e Tratamento de Não conformidades")}</div>
                <div className="texto"><TextoEditavel texto={relatorio.parecerDescricao} /></div>

                <table className="tabela-parecer">
                  <thead>
                    <tr>
                      <th>LOCAL</th>
                      <th>NÃO CONFORMIDADE</th>
                      <th>TRATAMENTO DA NÃO CONFORMIDADE</th>
                    </tr>
                  </thead>
                  {parecerGrupos.map((grupo, grupoIndex) => (
                    <tbody className="grupo-parecer-local" key={`${grupo.local}-${grupoIndex}`}>
                      {grupo.itens.map((item, itemIndex) => (
                        <tr key={itemIndex}>
                          {itemIndex === 0 ? (
                            <td className="local-cell" rowSpan={grupo.itens.length}>
                              {grupo.local}
                            </td>
                          ) : null}
                          <td><TextoEditavel texto={item.naoConformidade} /></td>
                          <td><TextoEditavel texto={item.tratamento} /></td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>

                {notas.filter((nota) => textoOuPadrao(nota)).map((nota, index) => (
                  <div className="nota-parecer" key={index}>
                    * <TextoEditavel texto={nota} />
                  </div>
                ))}

                {mostrarBoasPraticas ? (
                  <>
                    <div className="subsecao">{tituloSubsecao(numeroBoasPraticas, relatorio.boasPraticasTitulo, "Boas práticas")}</div>
                    <div className="texto"><TextoEditavel texto={relatorio.boasPraticas} /></div>
                  </>
                ) : null}

                <div className="subsecao">{numeroResultadoAceitacao}. Resultado/Aceitação.</div>
                <div className="texto"><TextoEditavel texto={relatorio.resultadoAceitacao} /></div>

                <div className="secao">{tituloSecao(numeroAnexos, "ANEXOS.")}</div>
                <div className="texto"><TextoEditavel texto={relatorio.anexos} /></div>

                <div className="secao">{tituloSecao(numeroConsideracoesFinais, "CONSIDERAÇÕES FINAIS.")}</div>
                <div className="texto"><TextoEditavel texto={relatorio.consideracoesFinais} /></div>

                <div className="linha-final" />
                <p className="texto texto-restricao">
                  Os resultados de ensaios e as considerações feitas neste relatório têm significado restrito aos locais informados, às amostras analisadas e às condições em que os ensaios foram executados.
                </p>

                <div className="assinatura">
                  <img src="/Images/assinaturaLeal.png" alt="Assinatura" />
                  <div className="assinatura-nome">Jose Carlos Lemes Leal</div>
                  <div>Eng. Civil Esp. CREA 203379/D - TO</div>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <RodapeRestrito />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <RelatorioFotografico relatorio={relatorio} fotos={fotosAnexo} />
    </div>
  );
}
